import { clean, currencyToWords, isStr, isValidDate, numberToWords, toBase26, toDate, toRomanNumeral, toStr } from "rant-utils";
import { Address } from "../types";
import { titleCase } from "title-case";
import { NameCustomScriptFunctions } from "../data-entry/runScript";

const currencyFormatOptions = {
	min: 2, 
	max: 4,
};


export function dateTimeValue(dt: Date) {
    if (!dt) return "";
    return dt.toISOString();
}
export function dateValue(dt: Date) {
	const str = dateTimeValue(dt);
	if (str.length < 10) return str;

	return str.substring(0, 10);
}

export function formatAusMobile(value: string) {
	if (value === undefined || value === null) return "";
	
	var result = value.replace(/ /g, '');
	if (result.length > 7) {
		result = result.substring(0, 0+4) + " " + result.substring(4, 4+3) + " " + result.substring(7);
	} else if (result.length > 4) {
		result = result.substring(0, 0+4) + " " + result.substring(4);
	}
	return result;
}

// NOTE: provide spaces in ascending order
export function formatAnyPhoneNumber(value: string, spacesAt: number[]) {
	// processing from last space back to start
	var result = value;
	for(var i=spacesAt.length-1;i>=0;i--) {
		const spaceIndex = spacesAt[i];
		if (result.length > spaceIndex) {
			result = result.substring(0, spaceIndex) + " " + result.substring(spaceIndex);
		}
	}
	return result;
}



const ausPhoneMobileSpaces = [4, 7];
const ausPhoneDefaultSpaces = [2, 6];
const ausPhoneWithCountryCodeSpaces = [3, 7];

export function formatAusPhoneNumber(value: string) {
    value = clean(value, "0123456789");

    if (value && value.startsWith("04")) {
        return formatAnyPhoneNumber(value, ausPhoneMobileSpaces);
    } else {
        if (value.length > 10) {
            return "+" + formatAnyPhoneNumber(value, ausPhoneWithCountryCodeSpaces);
        } else {
            return formatAnyPhoneNumber(value, ausPhoneDefaultSpaces);
        }
    }
}

export function formatNumber(val: number) {
	const n = Number(val);
	if (Number.isNaN(n)) return "";
	return n.toLocaleString();
}

export function formatCurrency(
	val: number,
	fractionalDigits?: {
		min?: number,
		max: number,
	} | any,
	currency?: string,
) {
    if (val === undefined || val === null) return "";
	
	let min = 2;
	let max = 2;
	if (fractionalDigits) {
		if (Number.isInteger(fractionalDigits)) {
			min = max = fractionalDigits as number;
		} else {
			if (fractionalDigits.min) min = fractionalDigits.min;
			if (fractionalDigits.max) max = fractionalDigits.max;	
		}
	}


	const n = Number(val);
	if (isNaN(n)) return "";

    var aDigits = n.toFixed((fractionalDigits !== undefined) ? max : 2).split(".");
	aDigits[0] = aDigits[0].split("").reverse().join("").replace(/(\d{3})(?=\d)/g, "$1,").split("").reverse().join("");

	let result = (currency !== undefined ? currency : "$") + aDigits.join(".");

	if (fractionalDigits && min && min < max) {
		// try collapse back to min number of digits
		// i.e. 1.5000 -> 1.50
		const iPoint = result.indexOf(".");
		if (iPoint > 0) {
			let fracDigitCount = result.length - iPoint - 1;
			while (fracDigitCount > min) {
				if (result.substring(result.length-1) !== '0') break;

				result = result.substring(0, result.length-1);
				fracDigitCount--;
			}
		}	
	}

	return result;
}

export function formatShareCurrency(value: number) {

    return formatCurrency(Number(value), {
		min: 2,
		max: 4,
	});

}

export function formatListNumber(value: number, style: string, contextPrefix: string) {
	let prefix = "";

	if (style.startsWith("*.")) {
		prefix = contextPrefix;
		style = style.substring(2);
	}

	const str = String(value);

	let result;

	if (style.indexOf("1") >= 0)
	{
		result = prefix + style.replace("1", str);
	} else if (style.indexOf("a") >= 0)	{
		result = prefix + style.replace("a", toBase26(value-1).toLocaleLowerCase());
	} else if (style.indexOf("A") >= 0)	{
		result = prefix + style.replace("A", toBase26(value-1).toUpperCase());
	} else if (style.indexOf("i") >= 0)	{
		result = prefix + style.replace("i", toRomanNumeral(value).toLowerCase());
	} else if (style.indexOf("I") >= 0)	{
		result = prefix + style.replace("I", toRomanNumeral(value).toUpperCase());
	} else if (style.indexOf("o") >= 0)	{
		// standard bullet
		result = prefix + style.replace("o", "•");
	} else {
		//unknown format
		result = prefix + str;
	}

	// if there is a dot in the number already then remove any trailing one 
	// e.g. if "4.1." then change to "4.1"
	const i = result.indexOf(".");
	if (i >= 0 && (i < result.length-1) && result.endsWith(".")) result = result.substring(0, result.length-1);

	return result;
}

export function formatACN(value: string) {
	if (!value) return "";
	let result = value.replace(/ /g, ""); // expecting 9 digits
	if (result.length > 9) result = result.substring(0, 9);
	if (result.length > 6) result = result.substring(0, 6) + " " + result.substring(6);
	if (result.length > 3) result = result.substring(0, 3) + " " + result.substring(3);
	return result;
}



export function individualName(n: any, format?: string) {

	let result;

	let name = (n.name) ? n.name : n;

	if (format) {
		result = formatName(name, format);
	} else {
		const values = [];
		if (name.title) values.push(name.title);
		if (name.given1) values.push(name.given1);
		if (name.given2) values.push(name.given2);
		if (name.given3) values.push(name.given3);
		if (name.family) values.push(name.family);

		result = values.join(" ");
	}

	if (!result) return "Not entered";

	return result;
}

export function companyName(c: any, singleLine?: boolean) {
	let result = "";
	if (c.name) result += c.name;

	let acn;
	if (c.acn) {
		if (singleLine) {
			result += " (ACN " + c.acn + ")";
		} else {
			result += "\nACN " + c.acn;
		}
	}

	if (!result) return "Not entered";

	return result;
}

export function trustName(t: any) {
	if (!t) return;

	return t.name;
}

export function entityName(ent: any, format?: string) {
	if (!ent) return;

	if (ent.entity) ent = ent.entity;

	if (ent.entityType === "individual") {
		if (ent.individual) {
			return individualName(ent.individual.name, format);
		} else {
			return "";
		}
	
	} else if (ent.entityType === "company") {
		const result = [];
		if (ent.company.name) {
			result.push(ent.company.name);
		} else {
			result.push("Not Entered");
		}
		if (ent.company.acn) {
			result.push("ACN " + ent.company.acn);
		}
		return result.join(", ");

	} else if (ent.entityType === "trust") {
		return ent.trust ? ent.trust.name : "";

	}
}

export function formatName(obj: any, format: string): string {

	function getIndividual() {
		if (obj.individual && obj.individual.name) {
			return obj.individual.name;

		} else if (obj.entity && obj.entity.individual && obj.entity.individual.name) {
			return obj.entity.individual.name;

		} else {
			return obj;

		}
	}

	function getCompany() {
		if (obj.company) {
			return obj.company;
		
		} else if (obj.entity && obj.entity.company) {
			return obj.entity.company;

		} else {
			return obj;
		
		}
	}

	function getTrust() {
		if (obj.trust) {
			return obj.trust;
		
		} else if (obj.entity && obj.entity.trust) {
			return obj.entity.trust;

		} else {
			return obj;
		
		}
	}

	let parts = [];

	if (format === "name") { // John James Doe
		const ind = getIndividual();
		if (ind.given1) parts.push(ind.given1);
		if (ind.given2) parts.push(ind.given2);
		if (ind.given3) parts.push(ind.given3);
		if (ind.family) parts.push(ind.family);

		if (parts.length <= 0) {
			// fallback to: try use company details if available
			const c = getCompany();
			let companyName = "";
			if (c.name) companyName = c.name;
			if (c.acn) {
				if (companyName.length > 0) companyName += ", ";
				companyName += "ACN " + c.acn;
			}
			if (companyName) parts.push(companyName);
		}

		if (parts.length <= 0) {
			// fallback to: trust
			const c = getTrust();
			if (c) {
				if (c.trusteeType === "individual") {
					parts.push(formatName(c.trustee, format));
				} else {
					parts.push(c.trusteeCompany.name);
					if (c.trusteeCompany.acn) parts.push(`ACN ${c.trusteeCompany.acn}`)
				}
			}

			parts.push("as trustee for");

			if (c.name) parts.push(c.name);
		}

	} else if (format === "name/full") { // Mr John James Doe
		const ind = getIndividual();
		if (ind.title) parts.push(ind.title);
		if (ind.given1) parts.push(ind.given1);
		if (ind.given2) parts.push(ind.given2);
		if (ind.given3) parts.push(ind.given3);
		if (ind.family) parts.push(ind.family);

	} else if (format === "name/full/no-title") { // John James Doe
		const ind = getIndividual();
		if (ind.given1) parts.push(ind.given1);
		if (ind.given2) parts.push(ind.given2);
		if (ind.given3) parts.push(ind.given3);
		if (ind.family) parts.push(ind.family);

	} else if (format === "name/full/split-family") { 
		const ind = getIndividual();
		if (ind.given1) parts.push(ind.given1);
		if (ind.given2) parts.push(ind.given2);
		if (ind.given3) parts.push(ind.given3);

		const givens = parts.join(" ");
		return toStr(ind.family) + "\n" + givens;

	} else if (format === "name/first-abbrev") { // Mr J J Doe
		const ind = getIndividual();
		if (ind.title) parts.push(ind.title);
		if (ind.given1) parts.push(ind.given1.substring(0, 1));
		if (ind.given2) parts.push(ind.given2.substring(0, 1));
		if (ind.given3) parts.push(ind.given3.substring(0, 1));
		if (ind.family) parts.push(ind.family);

	} else if (format === "name/family") { // Mr J J Doe
		const ind = getIndividual();
		if (ind.family) parts.push(ind.family);

	} else if (format === "name/given") { // Mr J J Doe
		const ind = getIndividual();
		if (ind.given1) parts.push(ind.given1);
		if (ind.given2) parts.push(ind.given2);
		if (ind.given3) parts.push(ind.given3);


	}

	return parts.join(" ");
}



export function defaultFormatAddress(a: Address) {
	const defaultFormat = {
		separator: "\n",
		careOf: false,
		localityNewLine: true,
		localityUppercase: true,
	};

	return formatAddress(a, defaultFormat);
}

function expandAusState(s: string) {
	if (!s) return s;

	const map = {
		NSW: "New South Wales",
		VIC: "Victoria",
		QLD: "Queensland",
		SA: "South Australia",
		WA: "Western Australia",
		TAS: "Tasmania",
		NT: "Northern Territory",
		ACT: "Australian Capital Territory"
	} as any;
	const found = map[s.toUpperCase()];
	return found ? found : s;
}

function formatCareOf(careOf: string) {
	if (careOf && !careOf.trim().toLowerCase().startsWith("c/")) {
		return "C/- " + careOf;
	}
	return careOf;
}

export function formatAddress(obj: any, options: {
	separator: string,
	careOf?: boolean,
	state?: boolean,
	postCode?: boolean,
	country?: boolean,
	localityNewLine?: boolean,
	localityUppercase?: boolean,
	lines?: boolean,
	smsfState?: boolean,
	stateOnly?: boolean,
}) {
	if (!obj) return "";

	if (!options) options = {} as any;

	let a = obj;
	if (!a.region) {
		if (a.entity) {
			a = a.entity;
		}

		if (a.individual && a.individual.address) {
			a = a.individual.address;
		} else if (a.company && a.company.address) {
			a = a.company.address;
		} else if (a.trust && a.trust.address) {
			a = a.trust.address;
		}
	}

	const parts = [];

	if (options.stateOnly) {
		return expandAusState(a.state);
	}

	if (options.lines) {
		if (options.careOf && a.careOf) parts.push(formatCareOf(toStr(a.careOf)));
		if (a.line2) parts.push(titleCase(toStr(a.line2).toLowerCase()));
		if (a.street) parts.push(titleCase(toStr(a.street).toLowerCase()));
		// parts.push(titleCase(toStr(a.locality)));
		// parts.push(titleCase(toStr(a.state)));
		// parts.push(titleCase(toStr(a.postCode)));

	} else {
		if ((options.careOf !== false) && a.careOf) {
			parts.push(titleCase(formatCareOf(toStr(a.careOf)).toLowerCase()));
		}

		if (a.line2) {
			parts.push(titleCase(toStr(a.line2).toLowerCase()));
		}
		if (a.street) {
			parts.push(titleCase(toStr(a.street).toLowerCase()));
		}
	}

	let localityLine = "";

	const isAus = a.country?.toLocaleLowerCase() === "australia";
	
	if (!isAus) {
		if (!options.smsfState) {
			localityLine = titleCase(toStr(a.locality).toLowerCase());
			if (options.country !== false) localityLine += " " + titleCase(toStr(a.country).toLowerCase());
		}

	} else {
		localityLine = titleCase(toStr(a.locality).toLowerCase());
		const spaces = (options.localityNewLine) ? "   " : " ";
		if (options.state !== false) localityLine += spaces + titleCase(toStr(a.state).toLowerCase());
		if (options.postCode !== false) localityLine += spaces + titleCase(toStr(a.postCode).toLowerCase());
	}

	if (options.smsfState) {
		if (isAus) {
			localityLine += ` in the State of ${expandAusState(a.state)}`;
		} else {			
			localityLine += `${a.locality} in ${titleCase(toStr(a.country).toLowerCase())}`;
		}
	}

	if (localityLine) {
		if (options.localityUppercase) localityLine = localityLine.toUpperCase();

		if (options.localityNewLine) {
			return parts.join(options.separator) + "\n" + localityLine;
		} else {
			parts.push(localityLine);
		}
	}

	return parts.join(options.separator);
}

export function entityAddress(ent: any) {
	if (!ent) return;

	if (ent.entityType === "individual") {
		return defaultFormatAddress(ent.individual.address);

	} else if (ent.entityType === "company") {
		return defaultFormatAddress(ent.company.address);
	
	} else if (ent.entityType === "trust") {
		return defaultFormatAddress(ent.trust.address);
	
	} else if (ent.country) {
		// it IS an address
		return defaultFormatAddress(ent);
	}
}	

export function formatPOB(obj: any) {
	// ideally just called from "entity"
	if (!obj) return undefined;
	
	let pob = obj;
	if (!pob.region) {
		if (pob.individual && pob.individual.pob && pob.individual.pob.region) {
			pob = pob.individual.pob;
		} else if (pob.entity && pob.entity.individual && pob.entity.individual.pob && pob.entity.individual.pob.region) {
			pob = pob.entity.individual.pob;
		}
	}

	let result = "";

	if (pob.region && pob.region.toLowerCase() === "australia") {
		result = titleCase(toStr(pob.suburb).toLowerCase()) + ", " + pob.state;

	} else if (pob.region && pob.region.toLowerCase() === "international") {
		result = titleCase(toStr(pob.locality).toLowerCase()) + ", " + pob.country;

	}

	return result;
}

export function formatDateCustom(val: any, format: string) {
	if (!val) return undefined;
	let dt = toDate(val);
	if (!isValidDate(val)) return undefined;

	let result = "";

	if (format === "date") {
		result += dt.getDate() + " " + dt.toLocaleDateString('default', { month: "long" }) + ", " + dt.getFullYear();

	} else if (format === "date/short") {
		result = `${dt.getDate().toString().padStart(2, '0')}/${(dt.getMonth() + 1).toString().padStart(2, '0')}/${dt.getFullYear()}`

	}

	return result;
}

export function join(a: any[], sep1?: string, sep2?: string) {
	if (!sep1) sep1 = ", ";
	if (!sep2) sep2 = " and ";

	if (!a || a.length <= 0) return "";

	const items = [];

	let i = 0;
	let iMax = a.length-1;
	for(let item of a) {
		if (i > 0) {
			if (i < iMax) {
				items.push(sep1);
			} else {
				items.push(sep2);
			}
		}
		items.push(item);
		i++;
	}

	return items.join("");
}

export function formatter(value: any, format: string, forceCase?: string) {
	
	let result = value;

	if (format === "number/include-words") {
		let words = numberToWords(value);
		if (words) {
			result = words + ` (${formatNumber(value)})`;
		} else {
			result = formatNumber(value);
		}

	} else if (format === "currency") {
		result = formatCurrency(value, currencyFormatOptions);

	} else if (format === "currency/include-words") {
		let words = currencyToWords(value);
		if (words) {
			result = words + ` (${formatCurrency(value, currencyFormatOptions)})`;
		} else {
			result = formatCurrency(value, currencyFormatOptions);
		}

	} else if (format === "address") {
		result = formatAddress(value, {
			separator: ", ",
			careOf: false,
			state: false, postCode: false, country: false,
		});

	} else if (format === "address/smsf") {
		result = formatAddress(value, {
			separator: ", ",
			careOf: false,
			state: false, postCode: false, country: false,
			smsfState: true,
		});

	} else if (format === "address/smsf/careOf") {
		result = formatAddress(value, {
			separator: ", ",
			careOf: true,
			state: false, postCode: false, country: false,
			smsfState: true,
		});

	} else if (format === "address/full") {
		result = formatAddress(value, {
			separator: "\n",
			careOf: false,
			localityNewLine: true,
			localityUppercase: true,
		});

	} else if (format === "address/full/careOf") {
		result = formatAddress(value, {
			separator: "\n",
			careOf: true,
			localityNewLine: true,
			localityUppercase: true,
		});


	} else if (format === "address/lines") {
		result = formatAddress(value, {
			lines: true,
			separator: "\n"
		});

	} else if (format === "address/lines+careOf") {
		result = formatAddress(value, {
			lines: true,
			careOf: true,
			separator: "\n"
		});
	
	} else if (format === "address/state") {
		result = formatAddress(value, {
			stateOnly: true,
			separator: "",
		});

	} else if (format === "pob") {
		result = formatPOB(value);

	} else if (format.startsWith("name")) {
		result = formatName(value, format);

	} else if (format.startsWith("date")) {
		result = formatDateCustom(value, format);

	} else if (format === "acn") {
		result = formatACN(value);

	} else if (format === "join") {
		result = join(value);

	}

	if (forceCase) {
		if (!isStr(result)) result = toStr(result);

		if (forceCase === "sentence") {
			result = titleCase(result, { sentenceCase: true });
		} else if (forceCase === "title") {
			result = titleCase(result);
		} else if (forceCase === "lower") {
			result = result.toLowerCase();
		} else if (forceCase === "upper") {
			result = result.toUpperCase();
		}
	}

	return result;
}



export function getEntityNameFromHolders(
	holdersContext: any,
	holders: string[],
	parties: any[],

	options?: {
		specificHolderIndex?: number,
		jointWords1?: string,
		jointWords2?: string,
		share?: any, // object containing the isBeneficiallyHeld attribute
	}
) {
	let contextObject;

	if (!holders) return undefined;

	let specificHolderIndex;
	let jointWords1, jointWords2;
	if (options) {
		specificHolderIndex = options.specificHolderIndex;
		jointWords1 = options.jointWords1;
		jointWords2 = options.jointWords2;
	}
	if (!jointWords1) jointWords1 = ", jointly held with ";
	if (!jointWords2) jointWords2 = ", and ";

	const ents = [];
	for (let i=0;i<holders.length;i++) {
		const h = holders[i];

		if (specificHolderIndex !== undefined && specificHolderIndex >= 0 && specificHolderIndex !== i) {
			continue;
		}

		// get corresponding party
		let party;
		for(let p of parties) {
			if (p.containerID === h) {
				party = p;
				break;
			}
		}

		const ent = party.entity;

		const name = NameCustomScriptFunctions.entityName(ent, "name/full/no-title");
		ents.push(name);
	}

	let result = ents[0];
	if (ents.length > 1) {
		result += jointWords1;
		result += ents[1];

		for(let i=2;i<ents.length;i++) {
			result += jointWords2 + ents[i];
		}
	}

	if (holdersContext) {
		const isBeneficiallyHeld = holdersContext.isBeneficiallyHeld;
		if (isBeneficiallyHeld) {
			const trustee = holdersContext.trustee;
			result += `\nas trustee for ${NameCustomScriptFunctions.individualName(trustee, "name/full/no-title")}`;
		}
	}

	return result;
}

